// Rastro compartido entre el listener de 'vite:preloadError' (App.tsx) y el
// GlobalErrorBoundary.
//
// Cuando un chunk lazy no baja, Vite dispara 'vite:preloadError' y, si nadie lo
// cancela, re-tira el error. App.tsx le hace preventDefault() a propósito: un
// reload forzado en medio de un flujo le vuela el estado al usuario (formularios
// abiertos, ediciones a medio hacer). El costo es que el helper de preload deja
// de tirar y el `import()` RESUELVE con `undefined`.
//
// Para los lazy con default export React lo delata solo ("reading 'default'", ya
// contemplado en el boundary). Pero los ~50 lazy con named export
// (`import('./X').then((m) => ({ default: m.X }))`) explotan ANTES, adentro del
// callback, con un "Cannot read properties of undefined (reading 'X')" que no se
// parece en nada a un error de chunk. El boundary lo tomaba como bug real:
// pantalla de crash sin auto-reload + alerta a #incidents (así llegó el de
// 'ServiceEditModal'). Con esta marca el boundary sabe que el chunk no llegó y
// aplica la recuperación silenciosa que ya tenía escrita.

interface PreloadErrorRecord {
  at: number;
  message: string;
}

// Ventana corta a propósito: no queremos que una falla de preload vieja tape un
// bug real. Y no hace falta que sea larga — un import() que rechazó deja la
// entrada del module map envenenada para esa URL en TODO el documento, así que el
// import que termina disparando el crash vuelve a emitir el evento en el mismo
// instante, sin pedirle nada a la red.
const RECENT_WINDOW_MS = 10_000;

let lastPreloadError: PreloadErrorRecord | null = null;

export const recordPreloadError = (payload: unknown): void => {
  lastPreloadError = {
    at: Date.now(),
    message: (payload as Error | undefined)?.message || 'vite:preloadError'
  };
};

export const recentPreloadError = (): PreloadErrorRecord | null => {
  if (!lastPreloadError) return null;
  return Date.now() - lastPreloadError.at <= RECENT_WINDOW_MS ? lastPreloadError : null;
};

/**
 * ¿El crash es un chunk que no llegó? Compartido entre el GlobalErrorBoundary y los
 * boundaries chicos que viven FUERA de él (p.ej. el modal de confirmación, que monta
 * en GlobalProviders, arriba del boundary de la app).
 */
export const isChunkLoadError = (error: unknown): boolean => {
  // Un chunk que recién falló al bajar convierte en error de chunk a CUALQUIER
  // cosa que reviente a continuación: con el preloadError cancelado el import()
  // resuelve `undefined` y el crash real es el TypeError de la fábrica del lazy
  // ("Cannot read properties of undefined (reading 'ServiceEditModal')"), que no
  // matchea ningún texto de acá.
  if (recentPreloadError()) return true;

  const message = String((error as Error | undefined)?.message || '').toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('chunkloaderror') ||
    message.includes('loading chunk') ||
    message.includes('is not a valid javascript mime type') ||
    message.includes('unable to preload') ||
    // React.lazy resolving a stale/missing chunk after a deploy: the dynamic
    // import yields undefined and React blows up reading `_result.default`.
    // Chrome phrasing:
    message.includes('_result.default') ||
    message.includes("reading 'default'") ||
    message.includes('reading "default"') ||
    message.includes("evaluating 'e._result") ||
    // Firefox phrasing: "can't access property "default", e._result is undefined"
    message.includes('can\'t access property "default"') ||
    message.includes('_result is undefined')
  );
};

// --- Escalera de recuperación ---------------------------------------------------
// Un solo reload alcanza casi siempre (Chrome, Android, Mac). En iPhone no: desde el
// 6/10 el `import()` de la ruta falla con «Importing a module script failed.» y sigue
// fallando en CADA recarga rápida de la misma pestaña durante 10 s a 3 min, con la
// MISMA deployment antes y después del reload (no es un index.html viejo: el HTML va
// siempre a la red) y se arregla solo o reabriendo la app. Lo que sobrevive a esas
// recargas es lo que la pestaña repite igual en cada una: el Service Worker que
// intercepta /assets (la misma instancia sigue viva entre recargas) y la ráfaga de
// <link rel="modulepreload"> que cada ruta lazy dispara desde el 6/10 (75 en /agenda,
// 66 en ingresar reserva). Antes la pantalla terminal salía al segundo fallo y el botón
// hacía exactamente el mismo reload.
//
// Ahora: 1) reload con la URL nueva; 2) si vuelve a fallar, MODO SEGURO para esta
// pestaña (sin Service Worker ni cachés, sin la ráfaga de modulepreload: la ruta baja
// en cascada como antes del 6/10) y reload con un respiro; 3) recién ahí la pantalla.
export type ChunkRecoveryStep = 'reload' | 'safe_reload' | 'screen';

export interface ChunkRecoveryClaim {
  step: ChunkRecoveryStep;
  /** Intento dentro de la ventana (1 = primer fallo). */
  attempt: number;
}

const RECOVERY_KEY = 'agora-chunk-recovery';
// Cuenta seguidos los fallos que llegan a menos de esto del anterior. Un fallo de chunk
// minutos después (otra ruta, otro deploy) arranca la escalera de nuevo.
const RECOVERY_WINDOW_MS = 60_000;

const stepForAttempt = (attempt: number): ChunkRecoveryStep =>
  attempt <= 1 ? 'reload' : attempt === 2 ? 'safe_reload' : 'screen';

/**
 * Qué hacer con un chunk que no llegó, según cuántas veces ya lo intentamos seguido en
 * esta pestaña. Registra el intento: llamarla ES reclamarlo.
 */
export const claimChunkRecovery = (now = Date.now()): ChunkRecoveryClaim => {
  try {
    const previous = JSON.parse(sessionStorage.getItem(RECOVERY_KEY) || 'null') as {
      attempts?: number;
      at?: number;
    } | null;
    const recent = !!previous && now - Number(previous.at || 0) <= RECOVERY_WINDOW_MS;
    const attempt = (recent ? Number(previous?.attempts || 0) : 0) + 1;
    const record = JSON.stringify({ attempts: attempt, at: now });
    sessionStorage.setItem(RECOVERY_KEY, record);
    // Read-back: un storage que no tira pero tampoco guarda haría recargar para siempre.
    if (sessionStorage.getItem(RECOVERY_KEY) !== record) throw new Error('sessionStorage no persiste');
    return { step: stepForAttempt(attempt), attempt };
  } catch {
    // Sin sessionStorage no hay cómo contar (ni modo seguro, que vive ahí). La marca que
    // reloadFresh deja en la URL dice si recién recargamos: así nunca entra en loop.
    let reloadedAt = 0;
    try {
      reloadedAt = Number(new URL(window.location.href).searchParams.get('_v') || 0);
    } catch {
      /* sin URL parseable: primer intento */
    }
    return now - reloadedAt <= RECOVERY_WINDOW_MS
      ? { step: 'screen', attempt: 2 }
      : { step: 'reload', attempt: 1 };
  }
};

// --- Modo seguro (por pestaña) ---------------------------------------------------
const SAFE_MODE_KEY = 'agora-chunk-safe-mode';

export const isChunkSafeMode = (): boolean => {
  try {
    return sessionStorage.getItem(SAFE_MODE_KEY) === '1';
  } catch {
    return false;
  }
};

const enterChunkSafeMode = (): void => {
  try {
    sessionStorage.setItem(SAFE_MODE_KEY, '1');
  } catch {
    /* sin storage la escalera ya no llega acá (claimChunkRecovery pasa a la pantalla) */
  }
};

/**
 * En modo seguro, los `<link rel="modulepreload">` que el helper de Vite agrega por cada
 * import() de una ruta no se insertan: el helper no los espera (solo espera CSS), así que
 * el import() sigue igual y sus dependencias bajan en cascada, como antes del 6/10. Los
 * del index.html no pasan por acá. Se llama una vez, antes de montar React.
 */
export const installChunkSafeMode = (): boolean => {
  if (!isChunkSafeMode() || typeof document === 'undefined' || !document.head) return false;
  const head = document.head;
  const append = head.appendChild;
  head.appendChild = function <T extends Node>(this: HTMLHeadElement, node: T): T {
    if (node instanceof HTMLLinkElement && node.rel === 'modulepreload') return node;
    return append.call(this, node) as T;
  };
  return true;
};

const RESET_TIMEOUT_MS = 3000;
const SAFE_RELOAD_MIN_DELAY_MS = 1500;

const settleWithin = (work: Promise<unknown>, ms: number): Promise<void> =>
  Promise.race([
    work.then(
      () => undefined,
      () => undefined
    ),
    new Promise<void>((resolve) => setTimeout(resolve, ms))
  ]);

/**
 * Saca al Service Worker del camino: desregistra (la pestaña actual sigue controlada hasta
 * irse; la próxima navegación ya va directo a la red) y borra las cachés del origen (todas
 * son del SW: `agora-assets` y las de workbox). Con tope de tiempo: si el SW está trabado,
 * `getRegistrations()` puede no volver nunca, y la recarga no puede quedar esperándolo.
 */
export const resetServiceWorker = (): Promise<void> => {
  const unregister = (async () => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister().catch(() => false)));
  })();
  const purge = (async () => {
    if (typeof caches === 'undefined') return;
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key).catch(() => false)));
  })();
  return settleWithin(Promise.all([unregister, purge]), RESET_TIMEOUT_MS);
};

/**
 * Ejecuta el paso de la escalera. `safe_reload`: modo seguro + SW y cachés fuera + un
 * respiro (lo que traba a iOS dura segundos) + reload con URL nueva. El SW se vuelve a
 * registrar en la próxima apertura de la app, no en esta pestaña.
 */
export const recoverFromChunkError = async (step: Exclude<ChunkRecoveryStep, 'screen'>): Promise<void> => {
  if (step === 'reload') {
    reloadFresh();
    return;
  }
  const startedAt = Date.now();
  enterChunkSafeMode();
  await resetServiceWorker();
  const wait = SAFE_RELOAD_MIN_DELAY_MS - (Date.now() - startedAt);
  if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
  reloadFresh();
};

// --- Alerta a Slack: una por pestaña ---------------------------------------------
// Cada «Actualizar ahora» que vuelve a fallar mostraba la pantalla otra vez y mandaba
// otra alerta (4 en 6 minutos del mismo negocio). PostHog sigue contando cada pantalla.
const ALERT_KEY = 'agora-chunk-alerted';
let alertedInThisDocument = false;

export const claimChunkAlert = (): boolean => {
  if (alertedInThisDocument) return false;
  alertedInThisDocument = true;
  try {
    if (sessionStorage.getItem(ALERT_KEY)) return false;
    sessionStorage.setItem(ALERT_KEY, String(Date.now()));
  } catch {
    /* sin storage: una alerta por documento */
  }
  return true;
};

// A plain window.location.reload() is NOT enough to recover from a stale
// bundle: iOS Safari frequently serves the document from its bfcache/disk
// cache without revalidating, so the user keeps getting the old index.html
// and stays stuck. Navigating to a brand-new URL (cache-busting query) defeats
// any cache and forces a fresh index.html with current chunk references.
export const reloadFresh = (): void => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_v', String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
};
